import React, { useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { coursesApi, learningPathsApi } from '../../api/client';

type Props = {
  employee: any;
  assignments: any[];
  results: any[];
  pathAssignments: any[];
  onBack: () => void;
};

function formatDate(value?: string | null) {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function hoursFromDuration(duration?: string | null) {
  if (!duration) return 0;
  const text = duration.toLowerCase();
  let minutes = 0;
  const parts = text.match(/(\d+(?:\.\d+)?)\s*(hour|hr|h|minute|min|m)/g) || [];
  parts.forEach((part) => {
    const num = parseFloat(part);
    if (/(hour|hr|h)/.test(part)) minutes += num * 60;
    else minutes += num;
  });
  return minutes / 60;
}

function isComplete(assignment: any) {
  return !!(assignment?.isTrainingComplete || assignment?.status === 'completed' || assignment?.trainingCompletedAt);
}

function employeeAnswerAt(answers: any, index: number, questionId?: string): number | string | null {
  if (answers == null) return null;
  if (Array.isArray(answers)) {
    const value = answers[index];
    if (value == null || value === '' || value === -1) return null;
    if (typeof value === 'number' || typeof value === 'string') return value;
    if (typeof value === 'object') return value.answer ?? value.selectedIndex ?? value.index ?? null;
    return null;
  }
  if (questionId && typeof answers === 'object' && answers[questionId] != null && answers[questionId] !== '') {
    return answers[questionId];
  }
  return null;
}

const EmployeeTrainingDetail: React.FC<Props> = ({ employee, assignments, results, pathAssignments, onBack }) => {
  const [viewMode, setViewMode] = useState<'list' | 'timeline'>('list');
  const [paths, setPaths] = useState<any[]>([]);
  const [expandedPathId, setExpandedPathId] = useState<string | null>(null);
  const [quizOpen, setQuizOpen] = useState<any | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [quizError, setQuizError] = useState<string | null>(null);

  const courseAssignments = assignments.filter((a) => a.assignmentSource !== 'learning_path');

  useEffect(() => {
    learningPathsApi.list().then(setPaths).catch(() => setPaths([]));
    try {
      const saved = localStorage.getItem(`employee_notes_${employee.id}`);
      if (saved) setNotes(JSON.parse(saved));
    } catch {
      setNotes({});
    }
  }, [employee.id]);

  const resultsFor = (assignmentId: string) =>
    results
      .filter((r) => r.assignmentId === assignmentId)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const latestResult = (assignmentId: string) => {
    const rows = resultsFor(assignmentId);
    return rows.length ? rows[rows.length - 1] : null;
  };

  const averageScore = results.length
    ? Math.round(results.reduce((sum, row) => sum + Number(row.score || 0), 0) / results.length)
    : 0;

  const totalHours = courseAssignments
    .reduce((sum, assignment) => sum + hoursFromDuration(assignment.course?.duration), 0)
    .toFixed(1);

  const completedCount = courseAssignments.filter(isComplete).length;

  const assignmentStatus = (assignment: any) => {
    if (isComplete(assignment)) return { label: 'Completed', color: 'success' as const };
    if (resultsFor(assignment.id).length) return { label: 'In Progress', color: 'warning' as const };
    return { label: 'Not Started', color: 'default' as const };
  };

  const openQuiz = async (assignment: any) => {
    if (!assignment.courseId) return;
    try {
      setQuizError(null);
      const course = await coursesApi.get(assignment.courseId);
      setQuizQuestions(course.quiz || []);
      setQuizOpen({ assignment, course, result: latestResult(assignment.id) });
    } catch (err: any) {
      setQuizError(err?.response?.data?.error || err.message);
    }
  };

  const saveNote = () => {
    if (!noteFor || !noteText.trim()) return;
    const next = { ...notes, [noteFor]: noteText.trim() };
    setNotes(next);
    localStorage.setItem(`employee_notes_${employee.id}`, JSON.stringify(next));
    setNoteFor(null);
    setNoteText('');
  };

  const timeline = useMemo(() => {
    const events = courseAssignments.map((assignment) => ({
      id: assignment.id,
      at: assignment.trainingCompletedAt || assignment.createdAt,
      title: assignment.course?.title || 'Course',
      detail: isComplete(assignment) ? 'Completed' : 'Assigned',
    }));
    return events.sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());
  }, [courseAssignments]);

  const courseRows = (rows: any[]) => rows.map((assignment) => {
    const attempts = resultsFor(assignment.id);
    const latest = attempts.length ? attempts[attempts.length - 1] : null;
    const started = attempts[0]?.createdAt || null;
    const status = assignmentStatus(assignment);
    return (
      <TableRow key={assignment.id}>
        <TableCell>{assignment.course?.title || 'Unknown Course'}</TableCell>
        <TableCell>{formatDate(assignment.createdAt)}</TableCell>
        <TableCell>{formatDate(started)}</TableCell>
        <TableCell>{formatDate(assignment.trainingCompletedAt || (isComplete(assignment) ? latest?.createdAt : null))}</TableCell>
        <TableCell><Chip label={status.label} color={status.color} size="small" /></TableCell>
        <TableCell>
          {latest ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>{latest.score}%</span>
              {latest.passed ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
            </Box>
          ) : 'N/A'}
        </TableCell>
        <TableCell>{attempts.length}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" title="View" onClick={() => openQuiz(assignment)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Add note"
              onClick={() => {
                setNoteFor(assignment.id);
                setNoteText(notes[assignment.id] || '');
              }}
            >
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Back to Training Status</Button>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant={viewMode === 'list' ? 'contained' : 'outlined'} onClick={() => setViewMode('list')}>List View</Button>
          <Button variant={viewMode === 'timeline' ? 'contained' : 'outlined'} onClick={() => setViewMode('timeline')}>Timeline View</Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => window.print()}>Export PDF</Button>
        </Box>
      </Box>

      {quizError && <Alert severity="error" sx={{ mb: 2 }}>{quizError}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>{employee.email || employee.name}</Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography>{employee.email || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Department</Typography>
              <Typography>{employee.department || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Hire Date</Typography>
              <Typography>{formatDate(employee.createdAt)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Total Training Hours</Typography>
              <Typography fontWeight={700}>{totalHours} hours</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Average Quiz Score</Typography>
              <Typography fontWeight={700} color="primary">{averageScore}%</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Total Assignments</Typography>
              <Typography>{courseAssignments.length}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Completed</Typography>
              <Typography color="success.main" fontWeight={700}>{completedCount}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {viewMode === 'timeline' ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Timeline</Typography>
            {timeline.length === 0 && <Typography color="text.secondary">No course activity yet.</Typography>}
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {timeline.map((event) => (
                <Box key={event.id} sx={{ p: 2, bgcolor: '#f7f7f7', borderRadius: 1 }}>
                  <Typography fontWeight={700}>{event.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{event.detail} · {formatDate(event.at)}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Course Assignments</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['Course Name', 'Assigned Date', 'Started Date', 'Completed Date', 'Status', 'Quiz Score', 'Attempts', 'Actions'].map((col) => (
                        <TableCell key={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {courseAssignments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">No assignments found</TableCell>
                      </TableRow>
                    ) : courseRows(courseAssignments)}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Learning Path Assignments</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={48} />
                      <TableCell>Learning Path</TableCell>
                      <TableCell>Assigned Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Completed Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pathAssignments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No learning path assignments</TableCell>
                      </TableRow>
                    ) : pathAssignments.map((pathAssignment) => {
                      const definition = paths.find((path) => path.id === pathAssignment.learningPathId);
                      const pathCourses = definition?.courses || [];
                      const linked = pathCourses.map((item: any) => {
                        const assignment = assignments.find((row) => row.courseId === item.courseId && row.learningPathId === pathAssignment.learningPathId)
                          || assignments.find((row) => row.courseId === item.courseId);
                        return assignment ? { ...item, assignment } : null;
                      }).filter(Boolean);
                      const done = linked.filter((item: any) => isComplete(item.assignment)).length;
                      const status = pathAssignment.status === 'completed' || (linked.length > 0 && done === linked.length)
                        ? 'completed'
                        : done > 0 || pathAssignment.status === 'in_progress'
                          ? 'in_progress'
                          : 'not_started';
                      const overdue = pathAssignment.dueDate && status !== 'completed' && new Date(pathAssignment.dueDate).getTime() < Date.now();
                      const open = expandedPathId === pathAssignment.id;
                      return (
                        <React.Fragment key={pathAssignment.id}>
                          <TableRow>
                            <TableCell>
                              {linked.length > 0 && (
                                <IconButton size="small" onClick={() => setExpandedPathId(open ? null : pathAssignment.id)}>
                                  <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              {pathAssignment.learningPath?.title || definition?.title || 'Learning Path'}
                              {linked.length > 0 && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({linked.length} courses)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{formatDate(pathAssignment.assignedDate || pathAssignment.createdAt)}</TableCell>
                            <TableCell>
                              <Typography color={overdue ? 'error' : 'text.primary'}>{formatDate(pathAssignment.dueDate)}</Typography>
                            </TableCell>
                            <TableCell>{formatDate(status === 'completed' ? pathAssignment.completedDate : null)}</TableCell>
                            <TableCell>
                              <Chip
                                label={status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In Progress' : 'Not Started'}
                                color={status === 'completed' ? 'success' : status === 'in_progress' ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                          {open && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Courses in this Learning Path</Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      {['Course Name', 'Assigned Date', 'Started Date', 'Completed Date', 'Status', 'Quiz Score', 'Attempts', 'Actions'].map((col) => (
                                        <TableCell key={col}>{col}</TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>{courseRows(linked.map((item: any) => item.assignment))}</TableBody>
                                </Table>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!quizOpen} onClose={() => setQuizOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Quiz Details - {quizOpen?.course?.title || quizOpen?.assignment?.course?.title || 'Course'}
        </DialogTitle>
        <DialogContent>
          {quizOpen?.course && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Course Details</Typography>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Duration</Typography>
                  <Typography>{quizOpen.course.duration || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Typography>{quizOpen.course.category || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Content</Typography>
                  <Typography>{quizOpen.course.contentType || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Passing Score</Typography>
                  <Typography>{quizOpen.course.passingScore ?? 'N/A'}%</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Quiz Result</Typography>
            {quizOpen?.result ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="body2">Score: {quizOpen.result.score}%</Typography>
                  <Typography variant="body2">|</Typography>
                  <Chip
                    label={quizOpen.result.passed ? 'Passed' : 'Failed'}
                    color={quizOpen.result.passed ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Attempted: {formatDate(quizOpen.result.createdAt)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This quiz has not been attempted yet.
              </Typography>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Questions</Typography>
          {quizQuestions.length === 0 && (
            <Typography color="text.secondary">No quiz questions on this course.</Typography>
          )}
          {quizQuestions.map((question, qIndex) => {
            const employeeAnswer = employeeAnswerAt(quizOpen?.result?.answers, qIndex, question.id);
            const numericAnswer = typeof employeeAnswer === 'number'
              ? employeeAnswer
              : (employeeAnswer != null && employeeAnswer !== '' && !Number.isNaN(Number(employeeAnswer))
                ? Number(employeeAnswer)
                : null);
            const isChoice = question.questionType !== 'fill_blank' && question.questionType !== 'short_answer';
            const isCorrect = isChoice
              ? numericAnswer !== null && numericAnswer === question.correctAnswer
              : employeeAnswer != null && String(employeeAnswer).trim().toLowerCase() === String(question.correctAnswerText || '').trim().toLowerCase();
            return (
              <Accordion key={question.id || qIndex} defaultExpanded sx={{ mt: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{qIndex + 1}. {question.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {isChoice ? (
                    <>
                      <Typography variant="body2" gutterBottom>Options:</Typography>
                      {(question.options || []).map((option: string, oIndex: number) => {
                        const isEmployeeAnswer = numericAnswer === oIndex;
                        const isCorrectAnswer = oIndex === question.correctAnswer;
                        return (
                          <Box
                            key={oIndex}
                            sx={{
                              px: 1.5,
                              py: 0.75,
                              mb: 0.5,
                              borderRadius: 1,
                              bgcolor: isCorrectAnswer ? 'success.main' : isEmployeeAnswer ? 'error.light' : 'transparent',
                              color: isCorrectAnswer ? 'common.white' : 'text.primary',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: 'inherit', fontWeight: isCorrectAnswer || isEmployeeAnswer ? 700 : 400 }}>
                              {String.fromCharCode(65 + oIndex)}. {option}
                              {isCorrectAnswer && ' ✓ (Correct Answer)'}
                              {isEmployeeAnswer && !isCorrectAnswer && ' ✗ (Your Answer)'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" gutterBottom>Correct Answer:</Typography>
                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
                        {question.correctAnswerText || 'N/A'}
                      </Typography>
                      {employeeAnswer != null && (
                        <Typography variant="body2" sx={{ mt: 1, color: isCorrect ? 'success.main' : 'error.main', fontWeight: 700 }}>
                          Employee answer: {String(employeeAnswer)} {isCorrect ? '✓' : '✗'}
                        </Typography>
                      )}
                    </>
                  )}
                  {employeeAnswer == null && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      No answer recorded for this question.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuizOpen(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!noteFor} onClose={() => setNoteFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Training note</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline minRows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveNote}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeTrainingDetail;
